import { Injectable, NgZone } from '@angular/core';

/**
 * The one clock of every reservation timer on the page (design §4.4): a single `setInterval` of one second, started with the
 * first listener and stopped with the last — so a page without carts runs no timer at all, and a timer that is gone (a row
 * scrolled away by a reload, a closed drawer) leaves nothing behind. It runs OUTSIDE the Angular zone: a ticking second must not
 * turn into a change-detection pass of the whole application — each timer repaints only itself (`ChangeDetectorRef.detectChanges`).
 * A listener that needs the zone (to open a dialog) enters it itself.
 */
@Injectable({ providedIn: 'root' })
export class CartTickerService {
  private listeners = new Set<() => void>();
  private timer?: ReturnType<typeof setInterval>;

  constructor(private zone: NgZone) {
  }

  /** How many timers listen — for the specs: «no leaks». */
  get listenerCount(): number {
    return this.listeners.size;
  }

  /** Starts listening; the returned function stops it (idempotent). */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    if (this.timer === undefined) {
      this.timer = this.zone.runOutsideAngular(() => setInterval(() => this.tick(), 1000));
    }
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0 && this.timer !== undefined) {
        clearInterval(this.timer);
        this.timer = undefined;
      }
    };
  }

  private tick(): void {
    // a copy: a listener may stop listening (or start) while the second is being handed round
    Array.from(this.listeners).forEach(listener => {
      try {
        listener();
      } catch (error) {
        // one broken timer must not stop the others — the failure is still reported, from a task of its own
        setTimeout(() => {
          throw error;
        });
      }
    });
  }
}
